import { useEffect, useMemo } from 'react';
import { applyTheme, type BrandInput, clearTheme, type GeneratedTheme, generateTheme } from '@athlete/ui';
import { useProto } from './store';

const CACHE_KEY = 'athlete.brand';

/** Paint the last known organization theme immediately (before the API answers) so there is no flash of default colours. */
export function primeTheme() {
  try {
    const cached = window.localStorage.getItem(CACHE_KEY);
    if (cached) applyTheme(generateTheme(JSON.parse(cached) as BrandInput));
  } catch {
    /* ignore: first run, blocked storage, or corrupt cache */
  }
  syncRootAttributes();
}

export function cacheBrand(brand: BrandInput | null) {
  try {
    if (brand) window.localStorage.setItem(CACHE_KEY, JSON.stringify(brand));
    else window.localStorage.removeItem(CACHE_KEY);
  } catch {
    /* ignore */
  }
}

function syncRootAttributes() {
  const { theme, density } = useProto.getState();
  const root = document.documentElement;
  if (theme === 'system') root.removeAttribute('data-theme');
  else root.setAttribute('data-theme', theme);
  root.setAttribute('data-density', density);
}

/** Applies the organization theme to the whole document; returns the generated theme (with any contrast adjustments). */
export function useOrgTheme(brand: BrandInput | null): GeneratedTheme {
  const brandMode = useProto((s) => s.brandMode);
  const theme = useProto((s) => s.theme);
  const density = useProto((s) => s.density);
  const effective = brandMode === 'default' ? null : brand;

  const generated = useMemo(() => generateTheme(effective), [effective]);

  useEffect(() => {
    if (effective) applyTheme(generated);
    else clearTheme();
    cacheBrand(effective);
  }, [effective, generated]);

  useEffect(() => {
    syncRootAttributes();
  }, [theme, density]);

  return generated;
}
